import React, { useState } from 'react';
import { getCurrentPosition, formatDistance } from '../services/geolocation';
import { searchNearbyBusStops, checkOverpassApiStatus } from '../services/overpassApi';
import { Button } from './ui/button';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';
import { Alert, AlertDescription } from './ui/alert';
import { Skeleton } from './ui/skeleton';
import { 
  MapPin, 
  Navigation, 
  Loader2, 
  CheckCircle2, 
  XCircle, 
  AlertCircle,
  List
} from 'lucide-react';

const MapTest = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [userLocation, setUserLocation] = useState(null);
  const [busStops, setBusStops] = useState([]);
  const [apiStatus, setApiStatus] = useState(null);

  const handleGetLocation = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const position = await getCurrentPosition();
      setUserLocation(position);
      
      const stops = await searchNearbyBusStops(
        position.latitude, 
        position.longitude, 
        100 // 只搜索 100m 內
      );
      setBusStops(stops);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCheckApi = async () => {
    setLoading(true);
    try {
      const status = await checkOverpassApiStatus();
      setApiStatus(status ? 'available' : 'unavailable');
    } catch (err) {
      setApiStatus('error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl">
      <Card className="shadow-lg">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-2xl">
            <MapPin className="h-6 w-6" />
            地圖功能測試
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-3 mb-6">
            <Button
              onClick={handleGetLocation}
              disabled={loading}
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  處理中...
                </>
              ) : (
                <>
                  <Navigation className="h-4 w-4 mr-2" />
                  獲取位置並搜索巴士站
                </>
              )}
            </Button>
            <Button
              variant="outline"
              onClick={handleCheckApi}
              disabled={loading}
            >
              檢查 Overpass API
            </Button>
          </div>

          {error && (
            <Alert variant="destructive" className="mb-4">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {apiStatus && (
            <Alert 
              variant={apiStatus === 'available' ? 'success' : apiStatus === 'error' ? 'destructive' : 'default'} 
              className="mb-4"
            >
              {apiStatus === 'available' ? (
                <CheckCircle2 className="h-4 w-4" />
              ) : (
                <XCircle className="h-4 w-4" />
              )}
              <AlertDescription>
                Overpass API 狀態: {
                  apiStatus === 'available' ? '可用' : 
                  apiStatus === 'unavailable' ? '不可用' : 
                  '檢查失敗'
                }
              </AlertDescription>
            </Alert>
          )}

          {loading && !userLocation && !apiStatus && (
            <div className="space-y-2">
              <Skeleton className="h-20 w-full" />
              <Skeleton className="h-20 w-full" />
            </div>
          )}

          {userLocation && (
            <Card className="mb-4">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Navigation className="h-5 w-5" />
                  您的位置
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">緯度:</span>
                    <span className="font-mono">{userLocation.latitude.toFixed(6)}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">經度:</span>
                    <span className="font-mono">{userLocation.longitude.toFixed(6)}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">精度:</span>
                    <span className="font-mono">{Math.round(userLocation.accuracy)} 米</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {busStops.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <List className="h-5 w-5" />
                  找到 {busStops.length} 個巴士站
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 max-h-[300px] overflow-y-auto">
                  {busStops.map((stop) => (
                    <div key={stop.id} className="p-3 border rounded-lg">
                      <div className="font-medium">{stop.name}</div>
                      <div className="text-sm text-muted-foreground mt-1">
                        距離: {formatDistance(stop.distance)}
                        {stop.tags.ref && (
                          <span className="ml-2">
                            編號: {stop.tags.ref}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default MapTest;
